import React, { useEffect, useState, useContext } from 'react';
import { Grid, Typography, CircularProgress, MenuItem, Select, FormControl, InputLabel, Card, Container } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { collection, getDocs,query,where } from 'firebase/firestore';
import { db } from '../firebase'; // Firestore instance
import AppContext from '../AppContext';

const CustomerReport = () => {
  const [barChartData, setBarChartData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('This Month');
  const { shopId, t = (key) => key } = useContext(AppContext);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const customerQuery = query(collection(db, 'customers'), where('shopId', '==', shopId));
      const querySnapshot = await getDocs(customerQuery);
      const customerData = {};
      const newCustomersByMonth = {};
      let oneTimeCustomers = 0;
      let repeatCustomers = 0;
      const tableDataTemp = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const { customerName, orderIds, phoneNumber, createDate } = data;
  
        const dateObj = createDate.toDate();
        const formattedDate = dateObj.toLocaleDateString('en-GB');
        const month = dateObj.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        const date = dateObj.toLocaleDateString();
        const hour = dateObj.getHours();
  
        // Filter Data Based on Selected Filter
        if (shouldIncludeData(dateObj)) {
          // Aggregate for bar chart (Top 10 Customers by Orders)
          const numOrders = orderIds.length;
          customerData[customerName] = (customerData[customerName] || 0) + numOrders;
  
          // Aggregate for line chart (New Customers Over Time)
          newCustomersByMonth[month] = (newCustomersByMonth[month] || 0) + 1;
  
          // Aggregate for pie chart (One-time vs. Repeat Customers)
          if (numOrders > 1) {
            repeatCustomers += 1;
          } else {
            oneTimeCustomers += 1;
          }
  
          // Ensure only distinct customers are added to tableDataTemp
          if (!tableDataTemp[customerName]) {
            tableDataTemp[customerName] = {
              id: customerName, // You might want to use a unique ID instead of the customerName
              customerName,
              phoneNumber,
              orderIDs: orderIds.map(order => order.orderID).join(', '), // Extract orderID and join them
              createDate: formattedDate,
            };
          }
        }
      });
  
      // Convert tableDataTemp object to an array for the table
      setTableData(Object.values(tableDataTemp));
  
      // Set data for charts
      const barData = Object.entries(customerData)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([name, orders]) => ({ name, orders }));
  
      const lineData = Object.entries(newCustomersByMonth).map(([month, count]) => ({
        month,
        count,
      }));
  
      const pieData = [
        { type: t('One-time'), value: oneTimeCustomers },
        { type: t('Repeat'), value: repeatCustomers },
      ];
  
      setBarChartData(barData);
      setLineChartData(lineData);
      setPieChartData(pieData);
      setLoading(false);
    };
  
    fetchData();
  }, [filter]);
  

  const shouldIncludeData = (date) => {
    const now = new Date();
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfThisYear = new Date(now.getFullYear(), 0, 1);
    const startOfTwoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), 1);
  
    switch (filter) {
      case 'This Month':
        return date >= startOfThisMonth;
      case 'This Year':
        return date >= startOfThisYear;
      case 'Within Two Years':
        return date >= startOfTwoYearsAgo;
      default:
        return true;
    }
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        {t('Customer Dashboard')}
      </Typography>

      <FormControl sx={{ minWidth: 120, marginBottom: 2 }}>
        <InputLabel id="filter-label">{t('Filter')}</InputLabel>
        <Select
          labelId="filter-label"
          id="filter-select"
          value={filter}
          label={t('Filter')}
          onChange={handleFilterChange}
        >
          <MenuItem value="This Month">{t('This Month')}</MenuItem>
          <MenuItem value="This Year">{t('This Year')}</MenuItem>
          <MenuItem value="Within Two Years">{t('Within Two Years')}</MenuItem>
        </Select>
      </FormControl>

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {/* Line Chart */}
          <Grid item xs={12} md={12}>
            <Card sx={{ padding: 2 }} variant='outlined'>
              <Typography variant="h6">{t('New Customer Sign-ups Over Time')}</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={lineChartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" name={t('New Customers')} stroke="#1976D2" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
          {/* Bar Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ padding: 2 }} variant='outlined'>
              <Typography variant="h6">{t('Top 10 Customers by Orders')}</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={barChartData}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 40, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={120} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="orders" name={t('Number of Orders')} fill="#6A1B9A" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
          {/* Pie Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ padding: 2 }} variant='outlined'>
              <Typography variant="h6">{t('Customer Frequency Distribution')}</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    dataKey="value"
                    nameKey="type"
                    innerRadius={60}
                    outerRadius={100}
                    label
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${entry.type}`} fill={index % 2 === 0 ? '#2E7D32' : '#C62828'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
          {/* Data Table */}
          <Grid item xs={12}>
            <Typography variant="h6">{t('Customer Data Table')}</Typography>
            <DataGrid
              rows={tableData}
              columns={[
                { field: 'customerName', headerName: t('Customer Name'), width: 200 },
                { field: 'phoneNumber', headerName: t('Phone Number'), width: 150 },
                { field: 'orderIDs', headerName: t('Order ID'), width: 150 },
                { field: 'createDate', headerName: t('Order Date'), width: 150 },
              ]}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              checkboxSelection
              disableSelectionOnClick
              autoHeight
            />
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default CustomerReport;
